import React from 'react';
import Icon from '../../../components/AppIcon';


const ProjectHeader = ({ 
  project, 
  onSendReminders, 
  onAddVendor, 
  onEditProject,
  onBackToProjects,
  onMarkComplete,
  onShareStack,
  isOnlineCategory 
}) => {
  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!project?.deadline) return null;
    const today = new Date();
    const deadline = new Date(project.deadline);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };

  const statItems = [
    {
      icon: 'Users',
      value: project?.totalVendors,
      label: 'Vendors Invited',
      gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)',
    },
    {
      icon: 'FileText',
      value: project?.receivedQuotes,
      label: 'Quotes Received',
      gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)',
    },
    {
      icon: 'Clock',
      value: project?.pendingQuotes,
      label: 'Pending',
      gradient: 'linear-gradient(135deg, #E0528A, #c026d3)',
    },
  ];

  return (
    <div
      className="rounded-2xl overflow-hidden mb-6"
      style={{ border: '1.5px solid #fde8d8', background: 'white', boxShadow: '0 2px 12px rgba(245,138,84,0.07)' }}
    >
      {/* Top bar */}
      <div
        className="px-6 pt-5 pb-4"
        style={{ borderBottom: '1px solid #fde8d8' }}
      >
        <button
          onClick={onBackToProjects}
          className="flex items-center gap-1.5 text-sm font-medium mb-4 transition-colors hover:opacity-70"
          style={{ color: '#F58A54' }}
        >
          <Icon name="ArrowLeft" size={15} />
          Back to Stacks
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          {/* Project Info */}
          <div className="flex items-start gap-4 flex-1">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name={project?.icon} size={28} color="white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-xl font-bold text-gray-900">{project?.name}</h1>
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  {project?.status}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{project?.description}</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Icon name="Tag" size={13} style={{ color: '#F58A54' }} />
                  <span>{project?.category}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  <Icon name="DollarSign" size={13} style={{ color: '#F06F6B' }} />
                  <span className="font-semibold text-gray-800">{project?.budget ? `$${project?.budget?.toLocaleString()}` : 'No budget set'}</span>
                </div>
                {project?.deadline && (
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <Icon name="Calendar" size={13} style={{ color: '#E0528A' }} />
                    <span>{formatDate(project?.deadline)}</span>
                    <span
                      className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                      style={{ background: '#fffbeb', color: '#b45309' }}
                    >
                      {getDaysRemaining()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <button
              onClick={onSendReminders}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors hover:bg-orange-50"
              style={{ borderColor: '#fde8d8', color: '#F58A54' }}
            >
              <Icon name="Send" size={14} />
              Remind ({project?.pendingQuotes})
            </button>
            <button
              onClick={onShareStack}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors hover:bg-purple-50"
              style={{ borderColor: '#e9d5ff', color: '#7c3aed' }}
            >
              <Icon name="Share2" size={14} />
              Share Stack
            </button>
            <button
              onClick={onAddVendor}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="UserPlus" size={14} color="white" />
              Add Vendor
            </button>
            <button
              onClick={onMarkComplete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 shadow-md"
              style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
            >
              <Icon name="CheckCircle" size={14} color="white" />
              Mark Stack Complete
            </button>
          </div>
        </div>
      </div>
      {/* Stats Row */}
      {!isOnlineCategory && (
        <div className="grid grid-cols-3 divide-x" style={{ borderColor: '#fde8d8' }}>
          {statItems?.map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-4">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: stat?.gradient }}
              >
                <Icon name={stat?.icon} size={17} color="white" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">{stat?.value}</div>
                <div className="text-xs text-gray-500">{stat?.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectHeader;